import { useState, useEffect, useRef } from 'react';

export default function StatsShowcase() {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({
    years: 0,
    clients: 0,
    products: 0,
    states: 0,
  });
  const sectionRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          setIsVisible(true);
          hasAnimated.current = true;
          animateCounters();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const animateCounters = () => {
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    const targets = {
      years: 2011,
      clients: 500,
      products: 1000,
      states: 28,
    };

    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);

      setCounts({
        years: Math.floor(targets.years * easeOutQuart),
        clients: Math.floor(targets.clients * easeOutQuart),
        products: Math.floor(targets.products * easeOutQuart),
        states: Math.floor(targets.states * easeOutQuart),
      });

      if (currentStep >= steps) {
        clearInterval(interval);
        setCounts(targets);
      }
    }, stepDuration);
  };

  const stats = [
    {
      icon: 'ri-time-line',
      value: counts.years,
      suffix: '',
      label: 'Established',
      description: 'Started with lamination pouches',
      gradient: 'from-teal-600 to-teal-700',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-700',
    },
    {
      icon: 'ri-user-smile-line',
      value: counts.clients,
      suffix: '+',
      label: 'Trade Partners',
      description: 'B2B clients across India',
      gradient: 'from-orange-500 to-orange-600',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
    },
    {
      icon: 'ri-stack-line',
      value: counts.products,
      suffix: '+',
      label: 'Product SKUs',
      description: 'Quality consumables',
      gradient: 'from-teal-600 to-teal-700',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-700',
    },
    {
      icon: 'ri-map-pin-line',
      value: counts.states,
      suffix: '',
      label: 'Pan-India Reach',
      description: 'States covered',
      gradient: 'from-orange-500 to-orange-600',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-12 md:py-20 lg:py-32 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-teal-600/10 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-700/5 rounded-full blur-3xl animate-spin-slow"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

      <div className="container mx-auto px-4 md:px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12 lg:mb-20">
          <div
            className={`inline-block mb-3 md:mb-4 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="px-4 py-2 bg-teal-600/20 text-teal-400 text-sm font-semibold rounded-full border border-teal-600/30">
              Our Impact
            </span>
          </div>
          <h2
            className={`text-2xl md:text-4xl lg:text-6xl font-bold text-white mb-3 md:mb-6 transition-all duration-1000 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Numbers That Speak
          </h2>
          <p
            className={`text-sm md:text-lg lg:text-xl text-gray-300 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Building trust through excellence and reliability
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 lg:gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`group relative transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${300 + index * 150}ms` }}
            >
              {/* Card */}
              <div className="relative bg-white/5 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 lg:p-8 border border-white/10 hover:border-white/30 transition-all duration-500 hover:transform hover:scale-105 hover:-translate-y-2 cursor-pointer overflow-hidden">
                {/* Gradient Overlay on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

                {/* Animated Ring */}
                <div className="absolute inset-0 rounded-xl md:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className={`absolute inset-0 rounded-xl md:rounded-2xl bg-gradient-to-br ${stat.gradient} blur-xl opacity-30 animate-pulse-ring`}></div>
                </div>

                {/* Content */}
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 flex items-center justify-center ${stat.iconBg} rounded-lg md:rounded-xl mb-3 md:mb-4 lg:mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 animate-float`} style={{ animationDelay: `${index * 200}ms` }}>
                    <i className={`${stat.icon} text-xl md:text-2xl lg:text-3xl ${stat.iconColor}`}></i>
                  </div>

                  {/* Number */}
                  <div className="mb-2 md:mb-3">
                    <span className="text-3xl md:text-4xl lg:text-6xl font-bold text-white group-hover:scale-110 group-hover:tracking-wider inline-block transition-all duration-500">
                      {stat.value}
                    </span>
                    <span className="text-2xl md:text-3xl lg:text-5xl font-bold text-teal-400 ml-0.5 md:ml-1">
                      {stat.suffix}
                    </span>
                  </div>

                  {/* Label */}
                  <h3 className="text-xs md:text-base lg:text-xl font-bold text-white mb-1 md:mb-2 group-hover:text-teal-400 transition-colors duration-300 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                    {stat.label}
                  </h3>

                  {/* Description */}
                  <p className="text-[10px] md:text-xs lg:text-sm text-gray-400 group-hover:text-gray-300 transition-colors duration-300 leading-tight truncate">
                    {stat.description}
                  </p>
                </div>

                {/* Corner Accent */}
                <div className={`absolute top-0 right-0 w-16 md:w-20 h-16 md:h-20 bg-gradient-to-br ${stat.gradient} opacity-10 rounded-bl-full transform translate-x-6 md:translate-x-8 -translate-y-6 md:-translate-y-8 group-hover:translate-x-4 group-hover:-translate-y-4 md:group-hover:translate-x-6 md:group-hover:-translate-y-6 transition-transform duration-500`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-8px);
          }
        }

        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.05);
          }
        }

        @keyframes spin-slow {
          from {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          to {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(0.95);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.5;
          }
          100% {
            transform: scale(0.95);
            opacity: 0.3;
          }
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }

        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }

        .animate-pulse-ring {
          animation: pulse-ring 2s ease-in-out infinite;
        }

        .bg-grid-pattern {
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
    </section>
  );
}
